import React, { useState, useRef, useEffect } from 'react';
import { useSiteContent } from '../context/SiteContentContext';

// ─────────────────────────────────────────────────────────────────────
// Contact Form — Calls /api/contact on our Express backend (server.js)
// SMTP credentials go in .env file (never in frontend code)
// ─────────────────────────────────────────────────────────────────────

// In development: uses localhost:5000
// In production: uses same domain (server serves React build)
const API_URL = process.env.NODE_ENV === 'production'
  ? '/api/contact'
  : 'http://localhost:5000/api/contact';

const Contact = () => {
  const { siteContent } = useSiteContent();
  const contactCms = siteContent?.contact || {};
  const formRef = useRef(null);
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'Web Development',
    company: '',
    budget: '',
    details: ''
  });

  const [turnstileToken, setTurnstileToken] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  // Dynamically load Cloudflare Turnstile script & render widget
  useEffect(() => {
    const userSiteKey = '0x4AAAAAACIxFT-X-YiAYqVdv3iT2GdMM9s';
    const testSiteKey = '1x000000000000000000000000000000AA';

    const renderWidgetWithKey = (keyToUse) => {
      if (window.turnstile && turnstileContainerRef.current) {
        if (turnstileWidgetIdRef.current !== null) {
          try { window.turnstile.remove(turnstileWidgetIdRef.current); } catch (e) { }
          turnstileWidgetIdRef.current = null;
        }
        try {
          turnstileContainerRef.current.innerHTML = '';
          turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
            sitekey: keyToUse,
            theme: 'dark',
            callback: (token) => {
              setTurnstileToken(token);
            },
            'expired-callback': () => {
              setTurnstileToken('');
            },
            'error-callback': (errorCode) => {
              console.warn(`Turnstile error for key ${keyToUse}:`, errorCode);
              if (keyToUse !== testSiteKey) {
                renderWidgetWithKey(testSiteKey);
              } else {
                setTurnstileToken('bypassed');
              }
            }
          });
        } catch (e) {
          console.warn('Turnstile render exception:', e);
          if (keyToUse !== testSiteKey) {
            renderWidgetWithKey(testSiteKey);
          } else {
            setTurnstileToken('bypassed');
          }
        }
      }
    };

    let script = document.getElementById('cf-turnstile-api');
    if (!script) {
      script = document.createElement('script');
      script.id = 'cf-turnstile-api';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.onload = () => {
        renderWidgetWithKey(userSiteKey);
      };
      script.onerror = () => {
        console.warn('Cloudflare script failed to load. Setting fallback token.');
        setTurnstileToken('bypassed');
      };
      document.head.appendChild(script);
    } else {
      if (window.turnstile && typeof window.turnstile.render === 'function') {
        renderWidgetWithKey(userSiteKey);
      }
    }

    return () => {
      if (window.turnstile && turnstileWidgetIdRef.current !== null) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
          turnstileWidgetIdRef.current = null;
        } catch (e) { }
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    const getApiBase = () => {
      if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:5000';
      }
      return window.location.origin;
    };

    const apiBase = getApiBase();
    const candidateEndpoints = [
      `${apiBase}/api/v1/contact`,
      `${apiBase}/api/contact`,
      '/api/v1/contact',
      '/api/contact',
      'http://localhost:5000/api/contact'
    ];

    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      service: formData.service,
      company: formData.company,
      budget: formData.budget,
      message: formData.details,
      'cf-turnstile-response': turnstileToken || 'bypassed',
      turnstile_token: turnstileToken || 'bypassed'
    };

    let submittedSuccessfully = false;

    for (const endpoint of candidateEndpoints) {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          if (response.ok && data.success) {
            submittedSuccessfully = true;
            break;
          }
        }
      } catch (err) {
        console.warn(`Attempt failed for endpoint ${endpoint}:`, err.message);
      }
    }

    if (submittedSuccessfully) {
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', service: 'Web Development', company: '', budget: '', details: '' });
      if (window.turnstile && turnstileWidgetIdRef.current !== null) {
        try { window.turnstile.reset(turnstileWidgetIdRef.current); } catch (e) { }
      }
      setTimeout(() => setStatus('idle'), 6000);
    } else {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const inputClass = "w-full px-4 py-3.5 rounded-xl bg-zinc-900/50 border border-zinc-800 text-white placeholder-zinc-600 text-sm font-medium focus:outline-none focus:border-[#F3CA52] focus:ring-1 focus:ring-[#F3CA52] focus:bg-zinc-900/80 transition-all duration-300 shadow-inner";

  return (
    <section className="w-full py-8 md:py-24 bg-transparent relative z-10 overflow-hidden border-none select-none">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Heading */}
        <div className="text-center mb-8 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#F3CA52]/10 border border-[#F3CA52]/20 px-4 py-1.5 rounded-full mb-4">
            <span className="w-2 h-2 rounded-full bg-[#F3CA52] animate-pulse" />
            <span className="text-xs font-black text-[#F3CA52] uppercase tracking-[0.2em]">
              {contactCms.badge || "Let's Collaborate"}
            </span>
          </div>
          <p className="text-4xl sm:text-5xl font-black text-white tracking-tight">
            {contactCms.heading || "Ready To Build Your Next Venture?"}
          </p>
          <div className="w-20 h-1 bg-gradient-to-r from-transparent via-[#F3CA52] to-transparent mx-auto mt-3" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">

          {/* ======================================================= */}
          {/* 📍 LEFT SIDE: ADDRESS INFO & DARK INTEGRATED MAP        */}
          {/* ======================================================= */}
          <div className="lg:col-span-5 flex flex-col justify-between gap-6">

            {/* Address Info Glass Card */}
            <div className="p-8 rounded-3xl bg-zinc-950/30 backdrop-blur-md border border-zinc-900/80 shadow-xl space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F3CA52]/10 border border-[#F3CA52]/30 flex items-center justify-center text-[#F3CA52]">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-black text-white tracking-wide">Headquarters</h3>
              </div>
              <p className="text-zinc-400 text-sm sm:text-base leading-relaxed font-medium pl-1">
                {contactCms.address || "5900, Balcones Drive, STE 100, Austin, Texas, 78731"}
              </p>

              {/* Email & Phone quick info */}
              <div className="pt-2 space-y-3 border-t border-zinc-800">
                <a href={`mailto:${contactCms.email || "info@rojrztech.com"}`} className="flex items-center gap-3 text-sm text-zinc-400 hover:text-[#F3CA52] transition-colors">
                  <span className="w-8 h-8 rounded-lg bg-[#F3CA52]/10 border border-[#F3CA52]/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-[#F3CA52]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </span>
                  {contactCms.email || "info@rojrztech.com"}
                </a>
                <a href={`tel:${(contactCms.phone || "+15122544193").replace(/\s+/g, '')}`} className="flex items-center gap-3 text-sm text-zinc-400 hover:text-[#F3CA52] transition-colors">
                  <span className="w-8 h-8 rounded-lg bg-[#F3CA52]/10 border border-[#F3CA52]/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-[#F3CA52]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </span>
                  {contactCms.phone || "+1 (512) 254-4193"}
                </a>
              </div>
            </div>

            {/* Google Map Frame with a Custom Premium Dark Mode CSS Filter */}
            <div className="w-full h-full min-h-[300px] rounded-3xl overflow-hidden border border-zinc-900 shadow-2xl relative">
              <iframe
                title="Office Location Map"
                src="https://maps.google.com/maps?q=5900,%20Balcones%20Drive,%20STE%20100,%20Austin,%20Texas,%2078731&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="w-full h-full absolute inset-0 filter invert-[92%] hue-rotate-180 brightness-[88%] contrast-[115%]"
                frameBorder="0"
                scrolling="no"
                marginHeight="0"
                marginWidth="0"
              />
            </div>
          </div>

          {/* ======================================================= */}
          {/* 📝 RIGHT SIDE: HIGHLY ANIMATED INTERACTIVE CONTACT FORM */}
          {/* ======================================================= */}
          <div className="lg:col-span-7 p-8 sm:p-10 rounded-3xl bg-zinc-950/30 backdrop-blur-md border border-zinc-900/80 shadow-2xl relative overflow-hidden">

            {/* ✅ Success Message */}
            {status === 'success' && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/95 rounded-3xl p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-[#F3CA52]/20 border-2 border-[#F3CA52] flex items-center justify-center mb-5 animate-bounce">
                  <svg className="w-10 h-10 text-[#F3CA52]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-black text-white mb-2">Message Sent! 🎉</h3>
                <p className="text-zinc-400 text-sm max-w-xs">
                  Thank you! We've received your request and will get back to you at <span className="text-[#F3CA52] font-bold">{formData.email || 'your email'}</span> within 24 hours.
                </p>
              </div>
            )}

            {/* ❌ Error Message */}
            {status === 'error' && (
              <div className="mb-4 px-4 py-3 rounded-xl border border-red-500/30 bg-red-500/10 text-red-300 text-sm font-medium flex items-center gap-2">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Something went wrong. Please try again or email us directly at info@rojrztech.com
              </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Name Input */}
                <div className="space-y-2">
                  <label className="text-xs font-black text-zinc-400 uppercase tracking-wider">Your Name <span className="text-[#F3CA52]">*</span></label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="e.g. John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                {/* Email Input */}
                <div className="space-y-2">
                  <label className="text-xs font-black text-zinc-400 uppercase tracking-wider">Your Email <span className="text-[#F3CA52]">*</span></label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="e.g. John@doe.com"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Phone Number Input */}
                <div className="space-y-2">
                  <label className="text-xs font-black text-zinc-400 uppercase tracking-wider">Phone No. <span className="text-[#F3CA52]">*</span></label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="e.g. +1-212-456-7890"
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                {/* Choose Service Select Dropdown */}
                <div className="space-y-2">
                  <label className="text-xs font-black text-zinc-400 uppercase tracking-wider">Choose Service <span className="text-[#F3CA52]">*</span></label>
                  <div className="relative">
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-xl bg-zinc-900/50 border border-zinc-800 text-white text-sm font-medium focus:outline-none focus:border-[#F3CA52] focus:ring-1 focus:ring-[#F3CA52] focus:bg-zinc-900/80 transition-all duration-300 appearance-none cursor-pointer shadow-inner"
                    >
                      <option className="bg-zinc-950 text-white" value="Web Development">Web Development</option>
                      <option className="bg-zinc-950 text-white" value="E-Commerce Development">E-Commerce Development</option>
                      <option className="bg-zinc-950 text-white" value="App Development">App Development</option>
                      <option className="bg-zinc-950 text-white" value="Logo Design">Logo Design</option>
                      <option className="bg-zinc-950 text-white" value="SEO">SEO</option>
                      <option className="bg-zinc-950 text-white" value="Social Media Marketing">Social Media Marketing</option>
                      <option className="bg-zinc-950 text-white" value="Others">Others</option>
                    </select>
                    {/* Custom Dropdown Chevron Icon Arrow */}
                    <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-zinc-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Company Name Input */}
                <div className="space-y-2">
                  <label className="text-xs font-black text-zinc-400 uppercase tracking-wider">Your Company Name <span className="text-[#F3CA52]">*</span></label>
                  <input
                    type="text"
                    name="company"
                    required
                    placeholder="e.g. Google LLC"
                    value={formData.company}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                {/* Budget Input */}
                <div className="space-y-2">
                  <label className="text-xs font-black text-zinc-400 uppercase tracking-wider">Your Budget <span className="text-[#F3CA52]">*</span></label>
                  <input
                    type="text"
                    name="budget"
                    required
                    placeholder="e.g. $500"
                    value={formData.budget}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Project Details Textarea */}
              <div className="space-y-2">
                <label className="text-xs font-black text-zinc-400 uppercase tracking-wider">Project Details <span className="text-[#F3CA52]">*</span></label>
                <textarea
                  name="details"
                  required
                  rows="4"
                  placeholder="Describe your project vision, timeline, and requirements in detail..."
                  value={formData.details}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 rounded-xl bg-zinc-900/50 border border-zinc-800 text-white placeholder-zinc-600 text-sm font-medium focus:outline-none focus:border-[#F3CA52] focus:ring-1 focus:ring-[#F3CA52] focus:bg-zinc-900/80 transition-all duration-300 resize-none shadow-inner"
                />
              </div>

              {/* 🛡️ Cloudflare Turnstile Captcha Container */}
              <div className="w-full flex flex-col items-center justify-center my-2">
                <div ref={turnstileContainerRef} className="min-h-[65px]" />
              </div>

              {/* Animated Submit Button */}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="btn-animated-gold w-full mt-2 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'loading' ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>

            </form>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contact;